'use client'

import { useState } from 'react'

import type { CountryIsoCode } from '@src/i18n/locales'
import type { AddressDataset } from '@src/i18n/locales'
import type {
  AddressFormat,
  AddressSearch,
  ApiResponse,
  RegionLookup,
} from '@src/typings/address-search'
import { log } from '@src/utils/log.util'

const postalCodeLookupCache: Record<string, RegionLookup> = {}

export const searchAddress = async ({
  searchText,
  location,
  countryIso,
  addressDataset,
  options,
}: {
  searchText: string
  location: [string, string] | null
  countryIso: CountryIsoCode
  addressDataset: AddressDataset
  options?: Array<{ name: string; value: string }>
}) => {
  const searchAddressEndpoint = '/api/address/search'

  try {
    const formattedLocation = location ? `${location[0]},${location[1]}` : ''
    const res = await fetch(searchAddressEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        searchText,
        location: formattedLocation,
        countryIso,
        addressDataset,
        options,
      }),
    })
    const resJson: ApiResponse<AddressSearch> = await res.json()
    if (resJson.data) {
      const { suggestions } = resJson.data
      return suggestions
    }
    log.error(`Failed ${searchAddressEndpoint} - ${resJson?.message}`, {
      searchText,
      response: resJson,
    })
    return null
  } catch (error: any) {
    log.error(`Error ${searchAddressEndpoint} - ${error?.message}`, error)
    return null
  }
}

const getAddressByGlobalAddressKey = async ({ globalAddressKey }: { globalAddressKey: string }) => {
  const getAddressByKeyEndpoint = `/api/address/format/${globalAddressKey}`
  try {
    const res = await fetch(getAddressByKeyEndpoint)
    const resJson: ApiResponse<AddressFormat> = await res.json()
    if (resJson.data) {
      return resJson.data
    }
    log.error(`Failed ${getAddressByKeyEndpoint} - ${resJson.message}`, {
      globalAddressKey,
      response: resJson,
    })
    return null
  } catch (error: any) {
    log.error(`Error ${getAddressByKeyEndpoint} - ${error?.message} `, error)
    return null
  }
}

export const lookupPostalCode = async ({
  postalCode,
  countryIsoCode,
}: {
  postalCode: string
  countryIsoCode: CountryIsoCode
}) => {
  if (!postalCode || !countryIsoCode) return

  const searchAddressEndpoint = '/api/address/lookup'
  try {
    if (postalCodeLookupCache[postalCode]) {
      return postalCodeLookupCache[postalCode].suggestions
    }
    const res = await fetch(searchAddressEndpoint, {
      method: 'POST',
      body: JSON.stringify({ postalCode, countryIsoCode }),
    })
    const resJson: ApiResponse<RegionLookup> = await res.json()
    if (resJson.data) {
      postalCodeLookupCache[postalCode] = resJson.data
      const { suggestions } = resJson.data
      return suggestions
    }
    log.error(`Failed ${searchAddressEndpoint} - ${resJson.message} `, {
      postalCode,
      countryIsoCode,
      response: resJson,
    })
    return null
  } catch (error: any) {
    log.error(`Error ${searchAddressEndpoint} - ${error?.message}`, error)
    return null
  }
}

const useAddressSearch = () => {
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<AddressFormat | null>(null)
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<AddressFormat | null>(null)
  return {
    selectedShippingAddress,
    setSelectedShippingAddress,
    selectedBillingAddress,
    setSelectedBillingAddress,
    searchAddress,
    getAddressByGlobalAddressKey,
    lookupPostalCode,
  }
}

export default useAddressSearch
